import React, { useEffect, useState } from "react";
import StaffNavBar from "../components/StaffNavBar";
import {
  AdminPageBody,
  AdminPanel,
  AdminLoadingState,
  adminButton,
  adminPrimaryButton,
} from "../components/AdminPageShell";
import { useStaffNotifications } from "../store/staff";

const inputClass =
  "rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-800 outline-none focus:border-[#16213A] focus:ring-2 focus:ring-[#16213A]/15";

export default function StaffNotifications() {
  const {
    staffNotifications,
    staffNotificationsLoading,
    staffNotificationsError,
    staffNotificationsTotal,
    staffNotificationsPages,
    staffNotificationDetail,
    staffNotificationDetailLoading,
    staffNotificationDetailError,
    staffNotificationUnreadCount,
    staffNotificationUnreadCountError,
    staffNotificationMutationLoading,
    staffNotificationMutationError,
    fetchStaffNotifications,
    fetchStaffNotification,
    fetchStaffNotificationUnreadCount,
    markStaffNotificationRead,
    markAllStaffNotificationsRead,
  } = useStaffNotifications();
  const [page, setPage] = useState(1);
  const [status, setStatus] = useState("");
  const [type, setType] = useState("");
  const [showDetail, setShowDetail] = useState(false);

  const load = () => {
    fetchStaffNotifications({
      page,
      limit: 10,
      ...(status ? { status } : {}),
      ...(type ? { type } : {}),
    });
    fetchStaffNotificationUnreadCount();
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, status, type]);

  const open = async (id) => {
    const result = await fetchStaffNotification(id);
    if (result.success) {
      setShowDetail(true);
      const notification = result.data;
      if (!(notification.read || String(notification.status).toLowerCase() === "read")) {
        await markStaffNotificationRead(id);
        await fetchStaffNotificationUnreadCount();
      }
    }
  };

  return (
    <div>
      <StaffNavBar title="Notifications" context="View and manage your staff notifications." />
      <AdminPageBody>
        <div className="flex flex-wrap items-center gap-3">
          <div className="rounded-lg border border-slate-200 bg-white px-4 py-2 text-sm text-slate-700">
            Unread: <strong>{staffNotificationUnreadCount}</strong>
            {staffNotificationUnreadCountError && <span className="ml-2 text-red-700">{staffNotificationUnreadCountError}</span>}
          </div>
          <select className={inputClass} value={status} onChange={(event) => { setPage(1); setStatus(event.target.value); }}>
            <option value="">All statuses</option>
            <option value="unread">Unread</option>
            <option value="read">Read</option>
          </select>
          <input className={inputClass} placeholder="Filter by type" value={type} onChange={(event) => { setPage(1); setType(event.target.value); }} />
          <button
            className={`${adminPrimaryButton} ml-auto`}
            disabled={staffNotificationMutationLoading || staffNotificationUnreadCount === 0}
            onClick={async () => {
              const result = await markAllStaffNotificationsRead();
              if (result.success) fetchStaffNotificationUnreadCount();
            }}
          >
            Mark all read
          </button>
        </div>
        <AdminPanel className="overflow-hidden">
          {staffNotificationsError && <p className="px-4 text-sm text-red-700">{staffNotificationsError}</p>}
          {staffNotificationMutationError && <p className="px-4 text-sm text-red-700">{staffNotificationMutationError}</p>}
          {staffNotificationsLoading ? <AdminLoadingState /> : (
            <div className="overflow-x-auto">
              <table className="w-full text-left text-sm">
                <thead className="bg-slate-50 text-xs uppercase text-slate-500">
                  <tr><th className="px-5 py-3">Notification</th><th className="px-5 py-3">Type</th><th className="px-5 py-3">Status</th><th className="px-5 py-3">Date</th><th className="px-5 py-3 text-center">Action</th></tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {staffNotifications.map((notification) => (
                    <tr key={notification.id}>
                      <td className="px-5 py-3 font-medium text-slate-900">{notification.title || notification.message || notification.body || "Notification"}</td>
                      <td className="px-5 py-3 text-slate-600">{notification.type || "—"}</td>
                      <td className="px-5 py-3 text-slate-600">{notification.status || (notification.read ? "read" : "unread")}</td>
                      <td className="px-5 py-3 text-slate-600">{notification.createdAt || notification.created_at || "—"}</td>
                      <td className="px-5 py-3 text-center"><button className={adminButton} onClick={() => open(notification.id)}>View</button></td>
                    </tr>
                  ))}
                  {!staffNotifications.length && <tr><td colSpan={5} className="px-5 py-10 text-center text-slate-500">No notifications found.</td></tr>}
                </tbody>
              </table>
            </div>
          )}
          <div className="flex items-center justify-between border-t border-slate-200 p-4 text-sm text-slate-500">
            <span>Page {page} of {staffNotificationsPages} · {staffNotificationsTotal} notifications</span>
            <div className="flex gap-2">
              <button className={adminButton} disabled={page <= 1} onClick={() => setPage((value) => value - 1)}>Previous</button>
              <button className={adminButton} disabled={page >= staffNotificationsPages} onClick={() => setPage((value) => value + 1)}>Next</button>
            </div>
          </div>
        </AdminPanel>
        {showDetail && staffNotificationDetail && (
          <AdminPanel className="p-5">
            {staffNotificationDetailLoading ? <AdminLoadingState /> : staffNotificationDetailError ? (
              <p className="text-sm text-red-700">{staffNotificationDetailError}</p>
            ) : (
              <>
                <h2 className="mt-0 text-lg font-semibold text-slate-900">
                  {staffNotificationDetail.title || "Notification"}
                </h2>
                <p className="whitespace-pre-wrap text-sm text-slate-700">
                  {staffNotificationDetail.message || staffNotificationDetail.body || "No additional details."}
                </p>
                <button className={adminButton} onClick={() => setShowDetail(false)}>Close</button>
              </>
            )}
          </AdminPanel>
        )}
      </AdminPageBody>
    </div>
  );
}
